import { useApolloClient } from '@apollo/client';
import { uploadUserProfileImage } from '@choco-admin/utils';
import { Card, CardBody, Button } from '@heroui/react';
import React, { useState } from 'react';
import { FiArrowLeft } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';

import { useNotification } from '../../../../contexts/NotificationContext';
import {
  UsersPageGetUserDocument,
  type UsersPageGetUserQuery,
  type UsersPageGetUserQueryVariables,
} from '../../../../generated';
import { USER_VALIDATION_ERROR } from '../../constants';
import {
  useCreateUser,
  useBuyerLinking,
  useSupplierLinking,
} from '../../hooks';
import { UserForm } from '../UserForm';
import { UserFormData } from '../UserForm/hooks/useUserFormValidation';
import { UserFormChangedFields } from '../UserForm/utils/changedFields';

// Import UserFormData from the hook instead of duplicating

// A freshly created user's `profileImage.uploadUrl` is eventually consistent
// server-side — even a `network-only` re-fetch right after `userCreate` can
// still come back empty (verified locally: it's absent immediately, but
// present once the page is reloaded a moment later). Retry with backoff
// rather than treat one empty result as final.
const UPLOAD_URL_MAX_ATTEMPTS = 4;
const UPLOAD_URL_RETRY_DELAY_MS = 750;

const delay = (ms: number) =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });

// Each attempt must wait for the previous one before deciding whether to
// retry, so this loop is intentionally sequential rather than parallel.
const pollForProfileImageUploadUrl = async (
  apolloClient: ReturnType<typeof useApolloClient>,
  userId: string
): Promise<string | null> => {
  for (let attempt = 0; attempt < UPLOAD_URL_MAX_ATTEMPTS; attempt += 1) {
    if (attempt > 0) {
      // eslint-disable-next-line no-await-in-loop
      await delay(UPLOAD_URL_RETRY_DELAY_MS);
    }

    // eslint-disable-next-line no-await-in-loop
    const { data } = await apolloClient.query<
      UsersPageGetUserQuery,
      UsersPageGetUserQueryVariables
    >({
      query: UsersPageGetUserDocument,
      variables: { id: userId },
      fetchPolicy: 'network-only',
    });

    const uploadUrl = data.user?.profileImage?.uploadUrl;
    if (uploadUrl) {
      return uploadUrl;
    }
  }

  return null;
};

export const CreateUser: React.FC = () => {
  const navigate = useNavigate();
  const apolloClient = useApolloClient();
  const { addNotification } = useNotification();
  const { linkUserToBuyer, isLinking: isLinkingBuyer } = useBuyerLinking();
  const { linkUserToSupplier, isLinking: isLinkingSupplier } =
    useSupplierLinking();
  const { createUser, isLoading: isCreating } = useCreateUser({});
  const [error, setError] = useState<string | null>(null);

  const handleBack = () => {
    navigate('/users');
  };

  const validateForm = (values: UserFormData) => {
    const hasLinkedBuyers = (values.linkedBuyers || []).length > 0;
    const hasLinkedSupplier = values.supplier && !!values.supplierId;
    const isTestUserOrLinkedToAnEntity =
      values.isTest || hasLinkedBuyers || hasLinkedSupplier;

    return {
      isValid: isTestUserOrLinkedToAnEntity,
      shouldLinkBuyers: hasLinkedBuyers,
    };
  };

  const handleSubmit = async (
    values: UserFormData,
    _changedFields: UserFormChangedFields,
    pendingProfileImage?: HTMLCanvasElement | null
  ) => {
    setError(null);

    try {
      const validation = validateForm(values);

      if (!validation.isValid) {
        addNotification(USER_VALIDATION_ERROR);
        return;
      }

      const {
        linkedBuyers,
        loginMethod,
        orderPreferences,
        supplierId,
        ...otherValues
      } = values;

      // Map OrderPreferences to OrderPreferenceInput format
      const orderPreference = orderPreferences
        ? {
            pdf: orderPreferences.pdf ?? false,
            xlsx: orderPreferences.xlsx ?? false,
            sms: orderPreferences.sms || null,
            whatsApp: orderPreferences.whatsApp || null,
          }
        : undefined;

      const createInput = {
        name: otherValues.name,
        email: otherValues.email,
        phone: otherValues.phone,
        locale: otherValues.locale,
        isTest: otherValues.isTest ?? false,
        supplier: otherValues.supplier ?? false,
        supplierId: supplierId || null,
        loginMethod: loginMethod || 'phone',
        orderPreference,
      };

      const createdUser = await createUser(createInput);

      // The create mutation has no image input — a presigned uploadUrl is
      // the only place a picture chosen before creation can go, and it is
      // only reliably available a moment after creation (see the note on
      // UPLOAD_URL_MAX_ATTEMPTS above), so poll for it rather than trusting
      // a single re-fetch.
      if (pendingProfileImage) {
        try {
          const uploadUrl = await pollForProfileImageUploadUrl(
            apolloClient,
            createdUser.id
          );

          if (!uploadUrl) {
            throw new Error('No upload link is available for this user yet.');
          }

          await uploadUserProfileImage(uploadUrl, pendingProfileImage);
        } catch (err) {
          addNotification({
            type: 'danger',
            title: 'Profile Picture Upload Failed',
            message:
              err instanceof Error
                ? err.message
                : 'Failed to upload the profile picture. You can add it from the user details page.',
          });
        }
      }

      // Handle entity linking
      let hasLinkingErrors = false;

      // Link to supplier if selected
      if (values.supplier && values.supplierId) {
        try {
          await linkUserToSupplier(
            {
              value: values.supplierId,
              label: values.supplierName || 'Selected Distributor',
            },
            createdUser.id
          );
        } catch (err) {
          hasLinkingErrors = true;
          addNotification({
            type: 'danger',
            title: 'Distributor Linking Failed',
            message:
              err instanceof Error
                ? err.message
                : `Failed to link user to supplier ${
                    values.supplierName || ''
                  }. Please try linking again from the user details page.`,
          });
        }
      }

      // Link to buyers if selected
      if (validation.shouldLinkBuyers && values.linkedBuyers) {
        const buyerLinkingResults = await Promise.allSettled(
          values.linkedBuyers.map((buyer) =>
            linkUserToBuyer(buyer, createdUser.id).catch((err) => {
              hasLinkingErrors = true;
              addNotification({
                type: 'danger',
                title: 'Buyer Linking Failed',
                message:
                  err instanceof Error
                    ? err.message
                    : `Failed to link user to buyer ${buyer.label}. Please try linking again from the user details page.`,
              });
              throw err; // Re-throw to mark this promise as rejected
            })
          )
        );

        // Check for any rejected promises
        buyerLinkingResults.forEach((result, index) => {
          if (result.status === 'rejected') {
            hasLinkingErrors = true;
          }
        });
      }

      // Show a summary notification if there were linking errors
      if (hasLinkingErrors) {
        addNotification({
          type: 'warning',
          title: 'User Created with Linking Issues',
          message:
            'The user was created successfully, but some entity linking operations failed. You can retry linking from the user details page.',
        });
      }

      navigate(`/users/${createdUser.id}`);
    } catch (err) {
      // Set local error state for inline display
      setError(err instanceof Error ? err.message : 'Failed to create user');
    }
  };

  return (
    <div className="h-full overflow-auto p-6">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button
            variant="flat"
            color="default"
            startContent={<FiArrowLeft className="h-4 w-4" />}
            onPress={handleBack}
          >
            Back to Users
          </Button>
          <div>
            <h1 className="title-large">Create User</h1>
            <p className="text-default-500 mt-1">
              Add a new user to the platform
            </p>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <Card className="border-danger-200 bg-danger-50">
            <CardBody>
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0">
                  <div className="w-5 h-5 bg-danger-500 rounded-full flex items-center justify-center">
                    <span className="text-white text-xs font-bold">!</span>
                  </div>
                </div>
                <div>
                  <p className="text-sm font-medium text-danger-800 mb-1">
                    Creation Failed
                  </p>
                  <p className="text-sm text-danger-700">{error}</p>
                </div>
              </div>
            </CardBody>
          </Card>
        )}

        {/* Form */}
        <Card className="bg-content1 border-default-200">
          <CardBody>
            <UserForm
              onSubmit={handleSubmit}
              isSubmitting={isCreating || isLinkingBuyer || isLinkingSupplier}
              submitLabel="Create User"
              onCancel={handleBack}
            />
          </CardBody>
        </Card>
      </div>
    </div>
  );
};
